import type {
  ActivityDefinition,
  AllergyIntolerance,
  AuditEvent,
  CarePlan,
  CareTeam,
  ChargeItem,
  ChargeItemDefinition,
  Claim,
  ClaimResponse,
  ClinicalImpression,
  Condition,
  Coverage,
  Device,
  DeviceMetric,
  DeviceUseStatement,
  DiagnosticReport,
  DocumentReference,
  Encounter,
  Flag,
  Group,
  Library,
  Location,
  Media,
  MedicationRequest,
  MedicationStatement,
  Observation,
  Organization,
  Patient,
  PlanDefinition,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  RelatedPerson,
  RiskAssessment,
  ServiceRequest,
  Task,
} from 'fhir/r4';

type SupportedResource =
  | Patient
  | Observation
  | DeviceUseStatement
  | Practitioner
  | Device
  | Encounter
  | Group
  | Organization
  | DiagnosticReport
  | RelatedPerson
  | Coverage
  | ServiceRequest
  | Media
  | ClinicalImpression
  | MedicationStatement
  | MedicationRequest
  | DeviceMetric
  | Claim
  | ClaimResponse
  | ChargeItemDefinition
  | ChargeItem
  | Task
  | Location
  | Condition
  | AllergyIntolerance
  | CareTeam
  | Library
  | CarePlan
  | PlanDefinition
  | ActivityDefinition
  | Questionnaire
  | QuestionnaireResponse
  | RiskAssessment
  | Flag
  | DocumentReference;

type SupportedResourceType = SupportedResource['resourceType'];
type SupportedOperation = 'Create' | 'Update';

type BaseAuditEventValue =
  Lowercase<`${AuditEvent['resourceType']}.${SupportedResourceType}.${SupportedOperation}`>;
type BaseAuditEventText = `${SupportedOperation} ${SupportedResourceType}`;

type SupportedPermissionOperation = 'Create' | 'Read' | 'Update';

type CustomSupportedPermissionName =
  | 'Read All Patients'
  | 'Read ValueSet ICD10'
  | 'Feature Preview Navigator'
  | 'Feature Preview Notes'
  | 'Video Consult Create'
  | 'Video Consult Invite'
  | 'Feature Preview Auth0'
  | 'Feature Preview AI'
  | 'Feature Preview AI Search'
  | 'Feature Preview Tracking'
  | 'Early Warning Score Alert'
  | 'Alert Comment Read'
  | 'Alert Comment Create'
  | 'Alert Tech Read'
  | 'Logistics'
  | 'Calibrate Device'
  | 'Read Claim Template'
  | 'Update Claim Template'
  | 'Create Claim Template'
  | 'Sign for practitioner MedicationRequest'
  | 'Create Communication'
  | 'Read Communication'
  | 'Admin tab access'
  | 'Lancet pathology access'
  | 'Clinical Impression Summary';

export type SupportedPermissionName =
  | CustomSupportedPermissionName
  | `${SupportedPermissionOperation} ${SupportedResourceType}`;

//TODO clean list, should only contain special permissions
type CustomGroupSecurityValue =
  | 'group.security.patient.read.all'
  | 'group.security.valueset.icd10.read'
  | 'group.security.featurepreview.navigator'
  | 'group.security.featurepreview.ai.search'
  | 'group.security.featurepreview.notes'
  | 'group.security.videoconsult.create'
  | 'group.security.videoconsult.invite'
  | 'group.security.featurepreview.auth0'
  | 'group.security.featurepreview.ai'
  | 'group.security.featurepreview.tracking'
  | 'group.security.alert.ews'
  | 'group.security.alert.comment-create'
  | 'group.security.alert.comment-read'
  | 'group.security.alert.tech-read'
  | 'group.security.logistics'
  | 'group.security.device.configure'
  | 'group.security.claimtemplate.read'
  | 'group.security.claimtemplate.update'
  | 'group.security.claimtemplate.create'
  | 'group.security.medicationrequest.sign-for-practitioner'
  | 'group.security.communication.create'
  | 'group.security.communication.read'
  | 'group.security.admin'
  | 'group.security.lancet'
  | 'group.security.clinicalimpression.summary';

export type GroupSecurityValue =
  | CustomGroupSecurityValue
  | Lowercase<`${Group['resourceType']}.Security.${SupportedResourceType}.${SupportedPermissionOperation}`>;

type CustomAuditEventValue =
  | 'auditevent.deviceusestatement.end'
  | 'auditevent.deviceusestatement.start'
  | 'auditevent.deviceusestatement.cancel'
  | 'auditevent.observation.end'
  | 'auditevent.observation.start'
  | 'auditevent.observation.cancel'
  | 'auditevent.patient.discharge'
  | 'auditevent.patient.readmit'
  | 'auditevent.claimtemplate.update'
  | 'auditevent.admission.resume'
  | 'auditevent.admission.pause'
  | 'auditevent.communication.create';

type CustomEventText =
  | 'Terminate DeviceUseStatement'
  | 'Start DeviceUseStatement'
  | 'Cancel DeviceUseStatement'
  | 'Terminate Observation'
  | 'Start Observation'
  | 'Cancel Observation'
  | 'Discharge Patient'
  | 'Readmit Patient'
  | 'Update Claim Template'
  | 'Resume Admission'
  | 'Pause Admission'
  | 'Create Communication';

export type AuditEventValue = BaseAuditEventValue | CustomAuditEventValue;
export type AuditEventText = BaseAuditEventText | CustomEventText;
